import React from "react";
import { ArrowUpRight, BookOpen } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import useDailyTheme from "@/hooks/useDailyTheme";
import { track } from "@/lib/analytics";

/**
 * VaAlemLeiaTambem, 3 cards com scripture_connections do payload /api/today.
 *
 * Cada card corresponde a uma função teológica:
 *  - fundamento: origem/promessa
 *  - desenvolvimento: revelação em Cristo
 *  - aplicacao: aplicação ao presente/futuro escatológico
 *
 * Link do card abre o capítulo em Bíblia externa (YouVersion, com parâmetro ARC).
 */
const FUNCTION_LABELS = {
  fundamento: "Fundamento",
  desenvolvimento: "Desenvolvimento",
  aplicacao: "Aplicação",
  aplicação: "Aplicação",
};

const FUNCTION_ORDER = ["fundamento", "desenvolvimento", "aplicacao", "aplicação"];

// Constrói URL de YouVersion (Bíblia.com) para a referência ARC.
// Fallback: link de busca no Google se não conseguir parsear.
function bibleUrl(reference) {
  if (!reference) return "#";
  // Extrai o livro e capítulo do texto "Fp 3.20-21 (ARC)"
  // YouVersion não tem ARC, então usamos a NAA ou NVI como fallback ecumênico.
  const clean = reference.replace(/\(ARC\)/i, "").trim();
  return `https://www.bible.com/pt/search/bible?q=${encodeURIComponent(clean)}`;
}

export default function VaAlemLeiaTambem() {
  const { theme } = useDailyTheme();
  const r = useReveal();

  const connections = theme?.scripture_connections || [];
  if (!connections.length) return null;

  // Ordenar segundo FUNCTION_ORDER
  const ordered = [...connections].sort((a, b) => {
    const ai = FUNCTION_ORDER.indexOf((a.theological_function || "").toLowerCase());
    const bi = FUNCTION_ORDER.indexOf((b.theological_function || "").toLowerCase());
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });

  return (
    <section
      id="va-alem"
      data-testid="va-alem-section"
      className="relative bg-white py-16 md:py-24 border-t border-[#0B1E36]/6"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div ref={r} className="reveal">
          {/* Header pastoral, sem pressão */}
          <div className="max-w-2xl mb-12 md:mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#C5A059]" aria-hidden />
              <span className="text-[0.68rem] uppercase tracking-[0.32em] text-[#C5A059] font-medium">
                Vá além, leia também
              </span>
            </div>
            <h2 className="font-serif-display text-[#0B1E36] text-[2rem] md:text-[2.75rem] lg:text-[3.25rem] leading-[1.06] tracking-[-0.024em] font-medium">
              Três passagens que{" "}
              <span className="italic text-[#C5A059]">tecem o mesmo fio</span>{" "}
              da Palavra de hoje.
            </h2>
            <p className="mt-5 text-[0.98rem] md:text-[1.08rem] text-[#1F1A17]/60 leading-[1.75] font-light max-w-xl">
              O devocional se aprofunda quando percorremos a Escritura em sua
              amplitude. Leia com calma: uma passagem por vez, sem pressa.
            </p>
          </div>

          {/* Cards das conexões teológicas */}
          <div
            data-testid="va-alem-cards"
            className="grid md:grid-cols-3 gap-5 md:gap-6"
          >
            {ordered.map((c, i) => {
              const funcKey = (c.theological_function || "").toLowerCase();
              const label = FUNCTION_LABELS[funcKey] || "Conexão";
              return (
                <a
                  key={`${c.reference}-${i}`}
                  href={bibleUrl(c.reference)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`va-alem-card-${i + 1}`}
                  onClick={() =>
                    track("scripture_connection_click", {
                      reference: c.reference,
                      function: funcKey,
                      position: i + 1,
                    })
                  }
                  className="group relative bg-[#F7F5F0] border border-[#0B1E36]/8 p-7 md:p-8 hover:border-[#C5A059]/60 transition-colors flex flex-col"
                >
                  {/* Badge função teológica */}
                  <span
                    className="inline-block text-[0.58rem] uppercase tracking-[0.28em] text-[#C5A059] bg-[#C5A059]/8 border border-[#C5A059]/30 px-2.5 py-1 w-fit"
                    data-testid={`va-alem-badge-${i + 1}`}
                  >
                    {label}
                  </span>

                  {/* Referência */}
                  <p
                    className="mt-5 font-serif-display text-[1.5rem] md:text-[1.65rem] text-[#0B1E36] leading-[1.15] tracking-[-0.015em] font-medium"
                    data-testid={`va-alem-ref-${i + 1}`}
                  >
                    {c.reference}
                  </p>

                  {/* Texto do versículo (2 linhas máx) */}
                  {c.verse_text && (
                    <p
                      className="mt-4 text-[0.92rem] text-[#1F1A17]/70 leading-[1.65] font-light italic line-clamp-3"
                      data-testid={`va-alem-text-${i + 1}`}
                    >
                      &ldquo;{c.verse_text}&rdquo;
                    </p>
                  )}

                  {/* CTA */}
                  <div className="mt-auto pt-6 flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.26em] text-[#0B1E36] group-hover:text-[#C5A059] transition-colors">
                    <BookOpen size={13} strokeWidth={1.6} />
                    <span>Ler capítulo</span>
                    <ArrowUpRight
                      size={13}
                      strokeWidth={1.6}
                      className="ml-auto transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
